/* eslint-disable max-len */
import { selectIsAllReadButtonDisabled } from '@newStore/documentUI/documentUISelectors';
import { logPerformance } from '@store/helpers/generalUtils';
import _, { debounce } from 'lodash';
import { isMatch } from '../helpers/documentHelpers';
import { getProposalForIssuedDate } from '../helpers/documentProposalsHelpers';
import { fillTreeNodeProposalViewModel } from './proposalViewModel';
import {
  getDocumentReferencedTerms,
  getDocumentTermOptions,
  getInheritedWebConfigurations,
  getNumberOfModifications,
  getReferenceFrameThemes,
  getWebsitesConfiguration,
  otherCreatorsInSuggestions,
} from './viewModelHelpers';

/**
 * Enriches the node with some extra $$ properties
 * The property name will be used as the extra property for the node.
 * This is expected to be a function. This function recieves: node, state, flat
 */

// DocumentVM: Unused properties: (13)
// [
//   '$$identifier',
//   '$$prefix',
//   '$$color',
//   '$$attachments',
//   'inheritedAccessRights',
//   'selectedAccessRights',
//   'isDownloadAttachmentsGroup',
//   '$$isCollapsed',
//   '$$children',
//   'isReadOnly',
//   'referenceRelationsFrom',
//   'referenceRelations',
// ];

const nodeExtensions = {
  $$isRoot: (node, state, flat) => {
    return flat.length === 0 || flat[0].key === node.key;
  },
  $$isHidden: (node) => {
    return (
      (node.$$typeConfig.hideInDocument && !node.$$isRoot) ||
      !node.$$typeConfig.type ||
      (node.$$parent &&
        (node.$$parent.$$isHidden ||
          node.$$parent.$$isCollapsed ||
          !node.$$parent.$$typeConfig.type ||
          node.$$parent.$$typeConfig.hideChildrenInDocument === true ||
          node.$$parent.$$typeConfig.hideChildrenInDocument?.some((c) => isMatch(node, c)) ||
          node.$$parent.proposal?.isDeleted))
    );
  },
  websitesConfiguration: (node, state) => {
    return getWebsitesConfiguration(node, state);
  },
  // only used in web/js/deleteConfirmations/menuItemWithReference.js
  inheritedWebConfigurations: (node, state) => {
    return getInheritedWebConfigurations(node, state);
  },
  proposal: fillTreeNodeProposalViewModel,
  // used only for an pro newsletter root...
  numberOfModifications: (node, state) => {
    return node.$$isRoot ? getNumberOfModifications(node, state) : null;
  },
  // linkedContent: (node, state) => {
  //   return getLinkedContent(node, state);
  // },
};

const accessedProperties = new Set();

const removedProperties = new Set([
  '$$identifier',
  '$$prefix',
  '$$color',
  '$$attachments',
  'inheritedAccessRights',
  'selectedAccessRights',
  'isDownloadAttachmentsGroup',
  '$$isCollapsed',
  'isReadOnly',
  'referenceRelations',
  'linkedTeasers',
  'modificationAfterLastRead',
  'selectedContacts',
  'expandedAuthors',
  'referenceRelationsTo',
  'attachmentResources',
  'isInTOC',
  '$$dropZones',
  '$$buildingBlocks',
  'imagesInGroup',
  '$$hash',
  '$$hoverOnCollapse',
  '$$isCollapsed',
  '$$validations',
  '$$editSections',
  'isDownloadAttachmentsGroup',
  'selectedAccessRights',
  'inheritedAccessRights',
  '$$attachments',
  '$$color',
  '$$selection',
  'linkedContent',
]);

const logUnusedProperties = debounce(() => {
  const unusedProperties = Object.keys(nodeExtensions).filter(
    (extension) => !accessedProperties.has(extension),
  );
  console.log('DocumentVM: Unused properties:', unusedProperties);
  console.log('DocumentVM: Accessed properties:', Array.from(accessedProperties));
}, 5000);

const handler = {
  get(target, prop, receiver) {
    accessedProperties.add(prop);
    logUnusedProperties();
    if (removedProperties.has(prop)) {
      throw new Error(`Property ${prop} has been removed from the view model.`);
    }
    // biome-ignore lint/style/noArguments: <explanation>
    return Reflect.get(...arguments);
  },
};

export const treeToFlatVM = logPerformance(
  'treeToFlatVM',
  (treeRoot, state, includeHiddens = false, rootState) => {
    if (includeHiddens) {
      return treeToFlatWithHiddens(treeRoot, state, rootState);
    }
    const flat = [];
    function addChildren(node) {
      if (node?.$$typeConfig?.type && !node.$$isHidden) {
        if (node.$$children && node.$$children.length > 0) {
          node.$$children
            .sort((a, b) => a.readorder - b.readorder)
            .map((child) => {
              const childVM = { ...child };
              childVM.$$parent = node;
              if (state) {
                Object.keys(nodeExtensions).forEach((extension) => {
                  childVM[extension] = nodeExtensions[extension](childVM, state, flat, rootState);
                });
              }
              if (!childVM.$$isHidden && childVM.$$typeConfig.type) {
                flat.push(new Proxy(childVM, handler));
              }
              return addChildren(childVM);
            });
        }
      }
      return node;
    }

    if (!treeRoot.$$isHidden) {
      const rootVM = { ...treeRoot };
      if (state) {
        Object.keys(nodeExtensions).forEach((extension) => {
          rootVM[extension] = nodeExtensions[extension](rootVM, state, flat, rootState);
        });
      }
      flat.push(new Proxy(rootVM, handler));
      addChildren(rootVM);
    }
    return flat;
  },
);

const getMarkExplanationReferences = (relationsMap) => {
  const markExplanationReferences = [...relationsMap.values()]
    .filter(
      (r) =>
        r.relationtype === 'REFERENCES' &&
        r.to.$$expanded &&
        r.to.$$expanded.type === 'MARK_EXPLANATION',
    )
    .map((r) => r.to.$$expanded);
  return _(markExplanationReferences)
    .uniqBy('key')
    .orderBy([(meRef) => meRef.title.toLowerCase()])
    .value();
};

/**
 * Creates a view model for the document tree main screen.
 * @param {object} documentSubState
 * @param {object} allowedAbilities
 */
export const generateDocumentViewModel = logPerformance(
  'generateDocumentViewModel',
  (documentSubState, allowedAbilities, areNewSliceExternalResourcesLoading, rootState) => {
    const { tree } = documentSubState;
    // let allBuildingBlocks = [];
    const isAllReadButtonDisabled = selectIsAllReadButtonDisabled(rootState);

    const flatWithHiddens = treeToFlatVM(
      tree,
      { ...documentSubState, viewModel: { ...documentSubState.viewModel, allowedAbilities } },
      true,
      rootState,
    );

    const flat = flatWithHiddens.filter((n) => {
      // if (n.$$parent) {
      //   console.log('Node:', n.key, n.$$parent.key, n.$$relation.key,
      //     n.proposal ? n.proposal.isSubmitted : undefined);
      // }
      return !n.$$isHidden || n.$$isRoot;
    });

    const document = flat[0];

    // flat.forEach((node) => {
    //   if (node.$$buildingBlocks) {
    //     allBuildingBlocks = [
    //       ...new Set([...allBuildingBlocks, ...node.$$buildingBlocks.map((o) => o.type)]),
    //     ];
    //   }
    // });

    // const documentValidationStatus = selectDocumentValidationStatus(rootState);
    // const isValid = documentValidationStatus !== 'INVALID' && documentValidationStatus !== 'UNKNOWN';

    const terms = getDocumentReferencedTerms(documentSubState);
    const loading = areNewSliceExternalResourcesLoading || !!documentSubState.gettingTreeAsLeaf;

    return {
      ...documentSubState.viewModel,
      flat,
      flatWithHiddens,
      document,
      loading,
      initialLoadOngoing: documentSubState.viewModel.initialLoadOngoing && loading, // initially set as true until loading turns to false, then we know the initial loading is over
      selectionCount: documentSubState.allSelections.length,
      itemsToSave: documentSubState.pendingActions.length,
      hasPendingActions: documentSubState.pendingActions.length > 0,
      // isSaving: isValid && documentSubState.saving.length > 0,
      // allBuildingBlocks,
      confirmDeleteMessages: documentSubState.allSelections.reduce((list, nodeKey, index) => {
        const node = flatWithHiddens.find((n) => n.key === nodeKey);
        if (node?.$$typeConfig.customConfirmDeleteMessage) {
          const confirmMessage = node.$$typeConfig.customConfirmDeleteMessage(
            node,
            documentSubState,
          );
          if (confirmMessage) {
            list.add(confirmMessage);
          }
        }
        if (node && node.$$typeConfig.confirmDelete === true) {
          if (index !== 0) {
            // sometimes you want to remove a lot of nodes and a node below needs confirmation
            list.add('Ben je zeker dat je deze informatie wilt verwijderen');
          } else {
            const ntInfo = node.$$typeConfig.information;
            list.add(
              `Ben je zeker dat je ${ntInfo.definiteArticle ? 'dit' : 'deze'} ${
                ntInfo.single.split(':')[0]
              } wilt verwijderen?`,
            );
          }
        }
        return list;
      }, new Set()),
      // toc: tocRows(flatWithHiddens, documentSubState),
      // isValid,
      documentSections: flatWithHiddens.filter((n) => ['SECTION', 'WEBPAGE2'].includes(n.$$type)),
      previewLocations: [
        ...(document?.$$typeConfig?.previewModes ? document.$$typeConfig.previewModes : []),
        ...(document.websitesConfiguration
          ? document.websitesConfiguration.map((o) => {
              return {
                type: 'URL',
                name: o.website.domain + o.path,
                location: `https://${o.website.domain}${o.path}?preview&redactie`,
              };
            })
          : []),
      ],
      terms,
      termOptions: getDocumentTermOptions(documentSubState, document, terms.local),
      isPublishAvailable:
        documentSubState.mode === 'EDIT' &&
        tree.$$typeConfig.allowSuggestions &&
        !tree.issued &&
        [...documentSubState.api.proposals.values()].length === 0,
      allReadButton: {
        isVisible: true, // documentSubState.mode === 'EDIT',
        isDisabled: isAllReadButtonDisabled,
      },
      suggestions: {
        ...documentSubState.viewModel.suggestions,
        isSuggesting: documentSubState.mode === 'SUGGESTING',
        isReviewing: documentSubState.mode === 'REVIEWING',
        countSuggestionsToSubmitSelected: documentSubState.proposalsToSubmit.length,
        countSuggestionsToReviewSelected: documentSubState.proposalsToReview.length,
        otherCreatorsInSuggestionsToSubmit: otherCreatorsInSuggestions(
          documentSubState.proposalsToSubmit,
          documentSubState,
        ),
        isAllPendingSuggestionsToSubmit:
          documentSubState.proposalsToSubmit.length ===
          documentSubState.allProposalsToSubmit.length,
        isAllPendingSuggestionsToReview:
          documentSubState.proposalsToReview.length ===
          documentSubState.allProposalsToReview.length,
        groupedProposalsToSubmitByAuthor: documentSubState.groupedProposalsToSubmitByAuthor.filter(
          (group) => !group.authors.includes(`/persons/${documentSubState.me.key}`),
        ),
        hasProposalForIssuedDate:
          getProposalForIssuedDate(documentSubState.proposalsToReview) !== undefined,
      },
      allowedAbilities,
      referenceFrameThemes: getReferenceFrameThemes(documentSubState),
      markExplanationReferences: getMarkExplanationReferences(
        documentSubState.apiWithPendingChanges.relations,
      ),
    };
  },
);

const treeToFlatWithHiddens = (treeRoot, state, rootState) => {
  const flat = [];
  const rootVM = { ...treeRoot };
  function addChildren(node) {
    if (node) {
      if (node.$$children && node.$$children.length > 0) {
        node.$$children
          .sort((a, b) => a.readorder - b.readorder)
          .map((child) => {
            const childVM = { ...child };
            childVM.$$parent = node;
            childVM.$$root = rootVM;
            Object.keys(nodeExtensions).forEach((extension) => {
              // apply every node extensions
              childVM[extension] = nodeExtensions[extension](childVM, state, flat, rootState);
            });
            flat.push(new Proxy(childVM, handler));
            return addChildren(childVM);
          });
      }
    }
    return node;
  }

  Object.keys(nodeExtensions).forEach((extension) => {
    rootVM[extension] = nodeExtensions[extension](rootVM, state, flat, rootState);
  });
  flat.push(new Proxy(rootVM, handler));
  addChildren(rootVM);
  return flat;
};
